import React, { useState } from "react";
import { Link } from "react-router-dom";
import { GsdPasswordField } from "@/components/auth/GsdPasswordField.jsx";
import { LoginBackToSite } from "@/components/auth/LoginBackToSite.jsx";

/**
 * Carte Connexion Shotokan — pattern GSD (PokerPUR card + MalikaFit back + GsdPasswordField).
 * Remplace LoginCard coquille-auth (FR hardcodé, liens sans espacement).
 */
export function LoginCard({
  logoAsset,
  title = "Connexion",
  subtitle = null,
  backToSite = null,
  identifierLabel = "Identifiant",
  secretLabel = "Mot de passe",
  passwordModeLabel = "Mot de passe",
  pinModeLabel = "NIP 4 chiffres",
  modeSwitchAriaLabel = "Mode de connexion",
  showPasswordLabel = "Afficher le mot de passe",
  hidePasswordLabel = "Masquer le mot de passe",
  authModes = ["password"],
  activeMode: initialMode = "password",
  pin4Field = { length: 4, display: "dots" },
  primarySubmit = { label: "Se connecter", loadingLabel: "Connexion…" },
  secondaryLinks = [],
  errorMessage = null,
  disabled = false,
  loading = false,
  onSubmit,
  testIdPrefix = "login-card",
}) {
  const [activeMode, setActiveMode] = useState(initialMode);
  const [identifier, setIdentifier] = useState("");
  const [secret, setSecret] = useState("");
  const showModeSwitch = authModes.includes("password") && authModes.includes("pin4");

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit?.({ identifier, secret, mode: activeMode });
  };

  return (
    <form
      className="cq-login-card karate-login-card"
      data-testid={testIdPrefix}
      onSubmit={handleSubmit}
      noValidate
    >
      {backToSite ? (
        <div className="karate-login-card__back">
          <LoginBackToSite
            to={backToSite.to || "/"}
            label={backToSite.label}
            ariaLabel={backToSite.ariaLabel}
            testId={backToSite.testId || "login-back-to-site"}
          />
        </div>
      ) : null}

      <header className="karate-login-card__head">
        {logoAsset ? (
          <img className="cq-login-card__logo karate-login-card__logo" src={logoAsset} alt="" />
        ) : null}
        <h1 className="cq-login-card__title karate-login-card__title">{title}</h1>
        {subtitle ? <p className="karate-login-card__subtitle">{subtitle}</p> : null}
      </header>

      <div className="cq-login-card__field karate-login-card__field">
        <label className="cq-login-card__label" htmlFor={`${testIdPrefix}-identifier`}>
          {identifierLabel}
        </label>
        <input
          id={`${testIdPrefix}-identifier`}
          className="cq-login-card__input"
          type="email"
          inputMode="email"
          autoComplete="username email"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          data-testid={`${testIdPrefix}-identifier`}
          required
          disabled={disabled || loading}
        />
      </div>

      {showModeSwitch ? (
        <div className="cq-login-card__mode-switch" role="group" aria-label={modeSwitchAriaLabel}>
          <button
            type="button"
            className={`cq-login-card__mode-btn${activeMode === "password" ? " cq-login-card__mode-btn--active" : ""}`}
            onClick={() => setActiveMode("password")}
          >
            {passwordModeLabel}
          </button>
          <button
            type="button"
            className={`cq-login-card__mode-btn${activeMode === "pin4" ? " cq-login-card__mode-btn--active" : ""}`}
            onClick={() => setActiveMode("pin4")}
          >
            {pinModeLabel}
          </button>
        </div>
      ) : null}

      {activeMode === "pin4" ? (
        <div className="cq-login-card__field karate-login-card__field">
          <label className="cq-login-card__label" htmlFor={`${testIdPrefix}-secret`}>
            {pin4Field.label || pinModeLabel}
          </label>
          <input
            id={`${testIdPrefix}-secret`}
            className="cq-login-card__input"
            type="password"
            inputMode="numeric"
            maxLength={pin4Field.length || 4}
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            data-testid={`${testIdPrefix}-secret`}
            disabled={disabled || loading}
          />
        </div>
      ) : (
        <div className="karate-login-card__field">
          <GsdPasswordField
            id={`${testIdPrefix}-secret`}
            label={secretLabel}
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            autoComplete="current-password"
            testIdPrefix={`${testIdPrefix}-secret`}
            inputTestId={`${testIdPrefix}-secret`}
            toggleTestId={`${testIdPrefix}-secret-toggle`}
            showLabel={showPasswordLabel}
            hideLabel={hidePasswordLabel}
            disabled={disabled || loading}
          />
        </div>
      )}

      {errorMessage ? (
        <p className="cq-login-card__error karate-login-card__error" role="alert" data-testid={`${testIdPrefix}-error`}>
          {errorMessage}
        </p>
      ) : null}

      <button
        type="submit"
        className="cq-login-card__submit karate-login-card__submit"
        disabled={disabled || loading}
        data-testid={`${testIdPrefix}-submit`}
      >
        {loading ? primarySubmit.loadingLabel || "Connexion…" : primarySubmit.label || "Se connecter"}
      </button>

      {secondaryLinks.length ? (
        <nav className="cq-login-card__secondary karate-login-card__secondary" aria-label="Liens secondaires">
          {secondaryLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="cq-login-card__secondary-link karate-login-card__secondary-link"
              data-testid={link.testId}
            >
              {link.label}
            </Link>
          ))}
        </nav>
      ) : null}
    </form>
  );
}
